import type { MeResponse as Me } from '@exam-prep/contracts';
import {
  NAV_GROUPS,
  opensProductTab,
  ROUTES,
  type NavGroupId,
  type NavIcon,
  type Route,
  type RouteDef,
} from './admin-routes';
import { hasPermission } from './permissions';

export type { NavGroupId, NavIcon } from './admin-routes';

export interface NavItem {
  id: string;
  href: string;
  label: string;
  icon: NavIcon;
  active: boolean;
  /** Small count shown next to the label (e.g. open review tasks). */
  badge?: number;
  /** What the badge counts, read out by screen readers ("open in review queue"). */
  badgeLabel?: string;
}

export interface NavGroup {
  id: NavGroupId;
  label: string;
  /** The label is only for screen readers (Home has a single item, so a heading would repeat it). */
  hideLabel?: boolean;
  items: NavItem[];
}

interface NavInput {
  me: Me | null | undefined;
  /** Hash route segments, e.g. ['products', '<id>', 'mocks']. */
  route: string[];
  /** Products the admin can read (GET /admin/products), used for the per-product pages. */
  products?: readonly { id: string }[] | undefined;
  /** Open review tasks (TodayDto.review.open), shown on Review. */
  reviewOpen?: number | undefined;
  /** Open support tickets across all queues, shown on Support. */
  supportOpen?: number | undefined;
}

const BADGE_LABEL = { review: 'open in review queue', support: 'open tickets' } as const;

/**
 * The admin navigation model: Home / Questions / Exams & mocks / Students / Money / Settings,
 * built from the route table. Visibility mirrors the permission checks of the old header
 * (question-bank pages need item.read, KB pages kb.read, ...); product pages are shown when some
 * readable product grants the permission. The API enforces permissions regardless.
 */
export function buildNav({ me, route, products = [], reviewOpen, supportOpen }: NavInput): {
  groups: NavGroup[];
  account: NavItem;
} {
  // Prefer the product being viewed, else the first product the admin can read.
  const [section, viewed] = route;
  const productIds = [
    ...(section === 'products' && viewed ? [viewed] : []),
    ...products.map((p) => p.id),
  ];
  const productFor = (permission: string) =>
    productIds.find((id) => hasPermission(me, permission, id));
  const badges = { review: reviewOpen, support: supportOpen };

  /** The sidebar item for a route, or null when this admin may not see it. */
  const toItem = (def: RouteDef): NavItem | null => {
    let productId = '';
    if (def.access.scope === 'global' && !hasPermission(me, def.access.permission)) return null;
    if (def.access.scope === 'product') {
      const found = productFor(def.access.permission);
      if (!found) return null;
      productId = found;
    }
    const count = def.badge ? badges[def.badge] : undefined;
    return {
      id: def.id,
      href: typeof def.href === 'string' ? def.href : def.href(productId),
      label: def.label,
      icon: def.icon,
      active: def.matches(route),
      ...(def.badge && count ? { badge: count, badgeLabel: BADGE_LABEL[def.badge] } : {}),
    };
  };

  const visible = ROUTES.flatMap((def) => {
    const item = toItem(def);
    return item ? [{ def, item }] : [];
  });
  // Products stays lit on a product page unless a product-tab entry of its own already is.
  const tabEntryActive = visible.some(({ def, item }) => opensProductTab(def) && item.active);
  for (const { def, item } of visible)
    if (def.id === 'products') item.active = section === 'products' && !tabEntryActive;

  const groups = NAV_GROUPS.map(({ id, label, hideLabel }) => ({
    id,
    label,
    ...(hideLabel ? { hideLabel } : {}),
    items: visible.filter(({ def }) => def.group === id).map(({ item }) => item),
  }));
  const account = visible.find(({ def }) => def.group === 'account');
  if (!account) throw new Error('admin route table has no account entry');
  return { groups: groups.filter((g) => g.items.length > 0), account: account.item };
}

/** Page title for the content header, from the route (Home when nothing matches). */
export function pageTitle(route: Route): string {
  const def = ROUTES.find((r) => r.matches(route));
  if (!def) return 'Home';
  return def.title?.(route) ?? def.label;
}
